"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader, Calculator, ArrowRight, RefreshCw } from "lucide-react";
function GuideForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [submittedData, setSubmittedData] = useState<{
    amount: string;
    calculationType: string;
  } | null>(null);
  const onFormSubmit = async (e: React.FormEvent) => {
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const amount = formData.get("amount") as string;
    const calculationType = formData.get("calculationType") as string;
    await handleSubmit(e);
    setSubmittedData({
      amount,
      calculationType
    });
  };
  const handleReset = () => {
    setSubmittedData(null);
    resetForm();
  };
  if (submittedData && isSubmitted && !submitError) {
    const baseAmount = parseFloat(submittedData.amount) || 0;
    const hstRate = 0.13;
    let hstAmount = 0;
    let finalTotal = 0;
    if (submittedData.calculationType === "add") {
      hstAmount = baseAmount * hstRate;
      finalTotal = baseAmount + hstAmount;
    } else {
      finalTotal = baseAmount / (1 + hstRate);
      hstAmount = baseAmount - finalTotal;
    }
    return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <Card className="border-emerald-200 bg-emerald-50/50 shadow-md overflow-hidden">
                    <CardHeader className="bg-emerald-600 text-white py-4">
                        <CardTitle className="text-lg font-default flex items-center gap-2">
                            <Calculator className="size-5" />
                            Calculation Results
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Base Amount</p>
                                <p className="text-2xl font-mono font-bold text-foreground">${baseAmount.toFixed(2)}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">HST Rate (Ontario)</p>
                                <p className="text-2xl font-mono font-bold text-foreground">13%</p>
                            </div>
                            <div className="space-y-1 border-t border-emerald-100 pt-4">
                                <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">HST Amount</p>
                                <p className="text-2xl font-mono font-bold text-emerald-600">${hstAmount.toFixed(2)}</p>
                            </div>
                            <div className="space-y-1 border-t border-emerald-100 pt-4">
                                <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Final Total</p>
                                <p className="text-2xl font-mono font-bold text-foreground">${finalTotal.toFixed(2)}</p>
                            </div>
                        </div>

                        <div className="mt-8 p-4 bg-white rounded-lg border border-emerald-100 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Estimated Annual Tax Impact</p>
                                    <p className="text-3xl font-mono font-extrabold text-emerald-700">
                                        ${(hstAmount * 12).toFixed(2)}
                                    </p>
                                </div>
                                <div className="bg-emerald-100 p-3 rounded-lg">
                                    <ArrowRight className="text-emerald-600 size-6" />
                                </div>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-2 italic">*Based on monthly recurring expense projection</p>
                        </div>
                    </CardContent>
                </Card>

                <div className="text-center">
                    <p className="text-emerald-800 text-sm font-default mb-4">{successMessage}</p>
                    <Button onClick={handleReset} variant="outline" className="w-full border-emerald-200 text-emerald-800 hover:bg-emerald-100 h-12 font-bold">
                        <RefreshCw className="mr-2 size-4" />
                        Calculate Again
                    </Button>
                </div>
            </div>;
  }
  return <Form onSubmit={onFormSubmit} className="space-y-5">
            <FormField control={control} name="amount" rules={{
      required: "Amount is required",
      pattern: {
        value: /^\d+(\.\d{1,2})?$/,
        message: "Please enter a valid currency amount"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default text-sm font-medium text-foreground">Base Amount ($)</FormLabel>
                        <FormControl>
                            <Input placeholder="0.00" {...field} className="h-16 text-3xl font-mono border-border bg-input focus-visible:ring-primary" />
                        </FormControl>
                        <FormMessage className="text-destructive" />
                    </FormItem>} />

            <FormField control={control} name="calculationType" rules={{
      required: "Please select a calculation type"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default text-sm font-medium text-foreground">Tax Operation</FormLabel>
                        <FormControl>
                            <div className="grid grid-cols-2 gap-2">
                                <Button type="button" variant={field.value === "add" ? "default" : "outline"} className={`h-12 font-default ${field.value === "add" ? "bg-emerald-600 hover:bg-emerald-700" : "border-border"}`} onClick={() => field.onChange("add")}>
                                    Add HST (13%)
                                </Button>
                                <Button type="button" variant={field.value === "remove" ? "default" : "outline"} className={`h-12 font-default ${field.value === "remove" ? "bg-destructive hover:bg-destructive/90" : "border-border"}`} onClick={() => field.onChange("remove")}>
                                    Remove HST
                                </Button>
                            </div>
                        </FormControl>
                        <FormMessage className="text-destructive" />
                        <input type="hidden" name="calculationType" value={field.value} />
                    </FormItem>} />

            <FormField control={control} name="emailReport" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
                        <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-primary data-[state=checked]:bg-primary" />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                            <FormLabel className="font-default text-sm font-normal text-muted-foreground cursor-pointer">
                                Email me a detailed tax breakdown report
                            </FormLabel>
                        </div>
                    </FormItem>} />

            {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-destructive text-sm font-default">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground border-none shadow-lg h-14 text-lg font-default font-bold mt-2 transition-all">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : 'Generate Calculation'}
            </Button>
        </Form>;
}
export default function FreeGuideSignup() {
  return <section data-section-id="255947" id="hst-calculation-engine" className="bg-background text-foreground py-16 md:py-24 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--card)_0%,_transparent_70%)] opacity-50 pointer-events-none" />
            <div className="container mx-auto max-w-xl relative z-10">
                <Card className="border-border border-2 shadow-2xl bg-card backdrop-blur-sm">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="font-default text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                            Ontario HST Engine
                        </CardTitle>
                        <p className="text-muted-foreground font-default mt-2">OntarioCalc provides accurate, easy-to-use tools</p>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <FormProvider formKey="hst_calculator_form_hst_calculator" formId="hst_calculator_form_hst_calculator" sectionName="hst-calculation-engine" defaultValues={{
            amount: "",
            calculationType: "add",
            emailReport: false
          }} validationRules={{
            amount: {
              required: true
            },
            calculationType: {
              required: true
            },
            emailReport: {
              required: false
            }
          }} submitText="Generate Calculation" successMessage="Calculation complete. Your tax breakdown has been generated.">
                            <GuideForm />
                        </FormProvider>
                    </CardContent>
                </Card>
            </div>
        </section>;
}